import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  units,
  IconButton,
  Grid,
  Typography,
} from '@ha/dsl';

import { OverlayProps } from '../types';
import { DialogTitle as DatePickerDialogTitle } from './Dialog/DialogTitle';

const useStyles = makeStyles()(theme => ({
  dialogRoot: {
    // Iphone 5/SE
    [theme.breakpoints.down(321)]: {
      borderRadius: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      maxWidth: '100%',
      maxHeight: 'none',
    },
  },
  dialogContentRoot: {
    padding: units.yx(0, 0.5),
    [theme.breakpoints.up('sm')]: {
      padding: 0,
    },
  },
  dialogTitleRoot: {
    padding: units.yx(0.75, 1),
  },
  closeButton: {
    position: 'relative',
    right: units.rem(-1),
  },
  dateLabel: {
    padding: units.yx(0, 1.5),
  },
  footer: {
    padding: units.rem(1),

    [theme.breakpoints.up('md')]: {
      padding: units.rem(0.5),
      paddingBottom: units.rem(1.5),
    },
  },
  dialogTitle: {
    [theme.breakpoints.down('md')]: {
      fontSize: units.rem(1.125),
    },
  },
  titleWrapper: {
    paddingTop: units.rem(0.25),
    paddingBottom: units.rem(0.25),
    marginBottom: units.rem(1),
    borderBottom: units.border(1, 'solid', theme.palette.grey[300]),

    [theme.breakpoints.up('md')]: {
      padding: units.trbl(1, 1.5, 0.8, 1.5),
      marginBottom: 0,
      borderBottom: 'none',
    },
  },
}));

const Transition = React.forwardRef(
  (
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
  ) => <Slide direction="up" ref={ref} {...props} />,
);

const DatePickerDialog: React.FC<OverlayProps> = ({
  children,
  focused,
  from,
  onFieldFocusChange,
  onOverlayClose,
  onOverlayExit,
  open,
  OverlayTitle,
  OverlayActions,
  OverlayInfo,
  to,
  formatDate,
  T,
  withoutDividers = false,
  dialogTitle,
  fullScreenDialog = false,
}) => {
  const { classes } = useStyles();

  return (
    <Dialog
      data-test-locator="DatePicker / Overlay / Dialog"
      maxWidth="md"
      fullScreen={fullScreenDialog}
      onClose={onOverlayClose}
      open={open}
      PaperProps={{
        classes: { root: classes.dialogRoot },
      }}
      TransitionComponent={Transition}
      TransitionProps={{
        onExit: onOverlayExit,
      }}
    >
      {dialogTitle ? (
        <DialogTitle className={classes.titleWrapper}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography.H3
                fontWeight="normal"
                className={classes.dialogTitle}
              >
                {dialogTitle}
              </Typography.H3>
            </Grid>

            <IconButton
              className={classes.closeButton}
              aria-label={T('close')}
              onClick={onOverlayClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
      ) : (
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <DatePickerDialogTitle
            from={from}
            focused={focused}
            onFieldFocusChange={onFieldFocusChange}
            to={to}
            formatDate={formatDate}
            T={T}
          />
        </DialogTitle>
      )}

      <DialogContent
        classes={{ root: classes.dialogContentRoot }}
        dividers={!withoutDividers}
      >
        {dialogTitle && (
          <Typography.Paragraph
            className={classes.dateLabel}
            align={fullScreenDialog ? 'center' : 'left'}
          >
            {`${formatDate('D MMM YYYY', from)} - ${formatDate(
              'D MMM YYYY',
              to,
            )}`}
          </Typography.Paragraph>
        )}
        {OverlayTitle}
        {children}
        {OverlayInfo}
      </DialogContent>
      <DialogActions className={dialogTitle && classes.footer}>
        {OverlayActions}
      </DialogActions>
    </Dialog>
  );
};

export { DatePickerDialog };
